/**
 * Nexacro/XPlatform 호환 API — 변환한 화면이 익숙한 메서드 이름 그대로 쓰도록 감싼다.
 *
 *   const ds = new NexaDataset(dataset)        // getRowCount, getColumn, setColumn, filter, keystring …
 *   const grd = new NexaGrid(grid, ds)          // setCellProperty, getCellText, oncellclick …
 *
 * 행 번호는 Nexacro 와 같은 "논리 행"(필터·정렬 반영, 삭제 행 제외) 0부터. 물리 행은 getPhysicalRow.
 * 수식(filter·getCase*·findRowExpr)은 OneGrid 수식 엔진 (== && || ! iif decode currow …).
 * keystring 의 G: 는 정렬 + 그룹마다 끝에 소계 행(ROWTYPE_GROUP=16, getRowLevel ≥ 1)을 논리 행으로 끼움.
 *  소계 값: 그룹 키 컬럼 = 키, 숫자 컬럼 = 합계(setColumnProp 으로 AVG·COUNT·MAX·MIN·NONE·TEXT), 나머지 = 빈 값.
 *  getSum·getCase*·findRow·extractRows 는 소계 행을 건너뜀. NexaGrid 는 이 논리 행 순서(insertRow 위치 포함)를 그대로 그림.
 */
import { RowState } from './dataset.js';
import { compileFormula } from './formula.js';
import { sortView } from './query.js';
const num = (v) => (v == null || v === '' ? NaN : typeof v === 'number' ? v : typeof v === 'boolean' ? (v ? 1 : 0) : Number(String(v).replace(/,/g, '')));
const truthy = (v) => !(v == null || v === false || v === 0 || v === '' || (typeof v === 'number' && isNaN(v)));
const same = (a, b) => (a == null || a === '') && (b == null || b === '') ? true : typeof a === 'number' || typeof b === 'number' ? num(a) === num(b) : String(a) === String(b);
/** keystring "S:+col-col2", "G:+dept S:-rslt" → { group, sort } */
export function parseKeystring(ks) {
    const out = { group: [], sort: [] };
    const s = (ks ?? '').trim();
    if (!s)
        return out;
    const parts = /^[GS]:/i.test(s) ? s.split(/(?=[GgSs]:)/) : ['S:' + s];
    for (const p of parts) {
        const kind = p[0].toUpperCase() === 'G' ? out.group : out.sort;
        for (const m of p.slice(2).matchAll(/([+-]?)\s*([^+\-,\s]+)/g))
            kind.push({ col: m[2], dir: m[1] === '-' ? 'desc' : 'asc' });
    }
    return out;
}
/** Nexacro Dataset 호환 어댑터 */
export class NexaDataset {
    constructor(ds, id = '') {
        this.ds = ds;
        this.id = id;
        /** 삭제·버림을 뺀 물리 행 (자연 순서, insertRow 위치 반영) */
        this.order = [];
        /** 논리 행 → 물리 행 (filter·keystring 반영). 음수 = 소계 행 grp[-(v+1)] */
        this.view = [];
        this.grp = [];
        this.props = new Map();
        this.filterStr = '';
        this.ks = '';
        this.pos = -1;
        this.handlers = new Map();
        this.deleted = [];
        this.silent = 0;
        this.enableevent = true;
        this.reset();
        this.unsub = ds.on(e => {
            if (this.silent)
                return;
            if (e.type === 'load') {
                this.reset();
                this.fireView();
            }
            else if (e.type === 'rowadd' && e.row != null && !this.order.includes(e.row)) {
                this.order.push(e.row);
                this.view.push(e.row);
                this.fireView();
            }
            else if (e.type === 'rowdelete' && e.row != null) {
                this.drop(e.row);
                this.fireView();
            }
        });
    }
    destroy() { this.unsub(); }
    reset() {
        this.order = [];
        this.deleted = [];
        for (let r = 0; r < this.ds.length; r++) {
            if (this.ds.isVisibleRow(r))
                this.order.push(r);
            else if (this.ds.rowState(r) === RowState.DELETE)
                this.deleted.push(r);
        }
        this.applyView();
        this.pos = this.view.length ? 0 : -1;
    }
    drop(phys) {
        const i = this.order.indexOf(phys);
        if (i >= 0)
            this.order.splice(i, 1);
        const j = this.view.indexOf(phys);
        if (j >= 0)
            this.view.splice(j, 1);
        if (this.ds.rowState(phys) === RowState.DELETE && !this.deleted.includes(phys))
            this.deleted.push(phys);
    }
    applyView() {
        let v = this.order;
        if (this.filterStr) {
            const f = compileFormula(this.filterStr), ds = this.ds;
            v = v.filter(r => truthy(f({ ds, row: r })));
        }
        const k = parseKeystring(this.ks), keys = [...k.group, ...k.sort].filter(x => this.ds.cols[x.col]);
        if (keys.length) {
            v = Array.from(sortView(this.ds, Uint32Array.from(v), keys));
        }
        this.view = v === this.order ? [...v] : v;
        this.grp = [];
        const G = k.group.filter(x => this.ds.cols[x.col]).map(x => x.col);
        if (G.length)
            this.view = this.withSubtotals(this.view, G);
    }
    /** 정렬된 행 사이에 소계 행 끼우기: 그룹이 끝날 때 안쪽 단계부터 (단계 L 의 getRowLevel = 그룹 수 - L) */
    withSubtotals(v, G) {
        const ds = this.ds, out = [], open = [];
        const keyOf = (r, L) => G.slice(0, L + 1).map(c => String(ds.get(r, c) ?? '')).join('\u0001');
        const close = (L) => {
            for (let l = open.length - 1; l >= L; l--) {
                const st = open[l];
                this.grp.push({ level: G.length - l, keys: Object.fromEntries(G.slice(0, l + 1).map(c => [c, ds.get(st.rows[0], c)])), rows: st.rows });
                out.push(-this.grp.length);
            }
            open.length = Math.min(open.length, L);
        };
        for (const r of v) {
            let L = 0;
            while (L < open.length && keyOf(r, L) === open[L].key)
                L++;
            close(L);
            for (let l = open.length; l < G.length; l++)
                open.push({ key: keyOf(r, l), rows: [] });
            for (const st of open)
                st.rows.push(r);
            out.push(r);
        }
        close(0);
        return out;
    }
    /** 소계 행 값 계산 방식 (Nexacro Column prop): SUM·AVG·COUNT·MAX·MIN·NONE·KEY·TEXT(text 고정 글자) */
    setColumnProp(col, prop, text) { this.props.set(col, { prop: String(prop).toUpperCase(), text }); this.fireView(); }
    groupValue(gi, col) {
        const g = this.grp[gi];
        if (!g || !col)
            return null;
        if (col in g.keys)
            return g.keys[col];
        const p = this.props.get(col) ?? { prop: this.ds.cols[col]?.kind === 'num' ? 'SUM' : 'NONE' };
        if (p.prop === 'TEXT')
            return p.text ?? '';
        if (p.prop === 'NONE' || p.prop === 'KEY')
            return null;
        const vs = g.rows.map(r => num(this.ds.get(r, col)));
        const kind = p.prop === 'AVG' ? 'avg' : p.prop === 'COUNT' ? 'count' : p.prop === 'MAX' ? 'max' : p.prop === 'MIN' ? 'min' : 'sum';
        return this.agg(kind, vs) ?? null;
    }
    /** NexaGrid 용: 논리 행 순서 (0 이상 = 물리 행, 음수 = 소계 -(k+1)) */
    viewItems() { return [...this.view]; }
    /** 소계 k 의 컬럼 값 */
    subtotalValue(k, col) { return this.groupValue(k, col); }
    /** 소계 행 단계 (일반 행 0) */
    getRowLevel(row) { const p = this.view[row]; return p != null && p < 0 ? this.grp[-p - 1]?.level ?? 0 : 0; }
    fireView() { this.onviewchanged?.(this); }
    fire(name, e) {
        if (!this.enableevent)
            return;
        let ret;
        const prop = this[name];
        if (prop)
            ret = prop(this, e);
        for (const h of this.handlers.get(name) ?? []) {
            const r = h(this, e);
            if (r !== undefined)
                ret = r;
        }
        return ret;
    }
    addEventHandler(name, fn) { const a = this.handlers.get(name) ?? []; a.push(fn); this.handlers.set(name, a); return a.length - 1; }
    removeEventHandler(name, fn) { const a = this.handlers.get(name); if (!a)
        return 0; const i = a.indexOf(fn); if (i >= 0)
        a.splice(i, 1); return i >= 0 ? 1 : 0; }
    // ── 행·컬럼 ──
    getRowCount() { return this.view.length; }
    /** 필터 무시 행 수 */
    getRowCountNF() { return this.order.length; }
    getDeletedRowCount() { return this.deleted.length; }
    getColCount() { return this.ds.columns.length; }
    getColID(i) { return this.ds.columns[i]?.id; }
    getColIndex(id) { return this.ds.columns.findIndex(c => c.id === id); }
    getPhysicalRow(row) { const p = this.view[row]; return p == null || p < 0 ? -1 : p; }
    /** 물리 행 → 논리 행 (없으면 -1) */
    getLogicalRow(phys) { return this.view.indexOf(phys); }
    colId(c) { return typeof c === 'number' ? this.ds.columns[c]?.id : c; }
    getColumn(row, col) { const p = this.view[row]; return p == null ? undefined : p < 0 ? this.groupValue(-p - 1, this.colId(col)) : this.ds.get(p, this.colId(col)); }
    getColumnNF(row, col) { const p = this.order[row]; return p == null ? undefined : this.ds.get(p, this.colId(col)); }
    getOrgColumn(row, col) { const p = this.view[row]; return p == null ? undefined : p < 0 ? this.groupValue(-p - 1, this.colId(col)) : this.ds.getOrig(p, this.colId(col)); }
    getDeletedColumn(row, col) { const p = this.deleted[row]; return p == null ? undefined : this.ds.getOrig(p, this.colId(col)); }
    setColumn(row, col, v) {
        const p = this.view[row], id = this.colId(col);
        if (p == null || p < 0 || !id || !this.ds.cols[id])
            return false; // 소계 행은 바꿀 수 없음
        const old = this.ds.get(p, id);
        const ok = this.ds.set(p, id, v);
        if (ok)
            this.fire('oncolumnchanged', { row, col: this.getColIndex(id), columnid: id, oldvalue: old, newvalue: this.ds.get(p, id) });
        return ok;
    }
    getRowType(row) {
        const p = this.view[row];
        if (p == null)
            return NexaDataset.ROWTYPE_EMPTY;
        if (p < 0)
            return NexaDataset.ROWTYPE_GROUP;
        const s = this.ds.rowState(p);
        return s === RowState.INSERT ? 2 : s === RowState.UPDATE ? 4 : s === RowState.DELETE ? 8 : 1;
    }
    /** 끝에 행 추가 → 논리 행 번호 */
    addRow() { return this.insertRow(this.view.length); }
    insertRow(at) {
        at = Math.max(0, Math.min(at, this.view.length));
        this.silent++;
        let p;
        try {
            p = this.ds.addRow();
        }
        finally {
            this.silent--;
        }
        // 자연 순서 자리: at 위치(소계 행이면 그다음 일반 행) 앞
        let before = this.view[at];
        for (let i = at; before != null && before < 0; i++)
            before = this.view[i + 1];
        const oi = before == null ? this.order.length : this.order.indexOf(before);
        this.order.splice(oi < 0 ? this.order.length : oi, 0, p);
        this.view.splice(at, 0, p);
        this.fireView();
        this.rowposition = at;
        return at;
    }
    deleteRow(row) {
        const p = this.view[row];
        if (p == null || p < 0)
            return false;
        this.silent++;
        try {
            this.ds.deleteRow(p);
        }
        finally {
            this.silent--;
        }
        this.drop(p);
        this.fireView();
        if (this.pos >= this.view.length)
            this.rowposition = this.view.length - 1;
        return true;
    }
    deleteAll() { for (let i = this.view.length - 1; i >= 0; i--)
        this.deleteRow(i); }
    /** 자료 비우기 (컬럼 정보 유지) */
    clearData() { this.silent++; try {
        this.ds.clear();
    }
    finally {
        this.silent--;
    } this.order = []; this.view = []; this.deleted = []; this.fireView(); this.rowposition = -1; }
    /** 변경 취소 (Nexacro reset) */
    reset2() { this.ds.revertAll(); }
    // ── 위치 ──
    get rowposition() { return this.pos; }
    set rowposition(v) {
        const nv = v < 0 || !this.view.length ? -1 : Math.min(v, this.view.length - 1);
        if (nv === this.pos)
            return;
        const e = { oldrow: this.pos, newrow: nv, reason: 0 };
        if (this.fire('canrowposchange', e) === false)
            return;
        this.pos = nv;
        this.fire('onrowposchanged', e);
    }
    // ── 필터·정렬 ──
    /** 수식 필터. '' 이면 해제 */
    filter(expr) {
        this.filterStr = (expr ?? '').trim();
        this.applyView();
        this.fireView();
        this.pos = -1;
        this.rowposition = this.view.length ? 0 : -1;
        return true;
    }
    get filterstr() { return this.filterStr; }
    set keystring(ks) { this.ks = ks ?? ''; this.applyView(); this.fireView(); }
    get keystring() { return this.ks; }
    updateSortGroup(ks) { if (ks != null)
        this.ks = ks; this.applyView(); this.fireView(); }
    // ── 찾기 ──
    findRow(col, value, start = 0, end = -1) {
        const id = this.colId(col), e = end < 0 ? this.view.length - 1 : Math.min(end, this.view.length - 1);
        for (let i = Math.max(0, start); i <= e; i++)
            if (this.view[i] >= 0 && same(this.ds.get(this.view[i], id), value))
                return i;
        return -1;
    }
    findRowExpr(expr, start = 0, end = -1) {
        const f = compileFormula(expr), ds = this.ds, e = end < 0 ? this.view.length - 1 : Math.min(end, this.view.length - 1);
        for (let i = Math.max(0, start); i <= e; i++)
            if (this.view[i] >= 0 && truthy(f({ ds, row: this.view[i] })))
                return i;
        return -1;
    }
    extractRows(expr) {
        const f = compileFormula(expr), ds = this.ds, out = [];
        this.view.forEach((p, i) => { if (p >= 0 && truthy(f({ ds, row: p })))
            out.push(i); });
        return out;
    }
    // ── 집계 ──
    values(col, expr, start, end) {
        const ds = this.ds, f = expr ? compileFormula(expr) : null;
        const g = ds.cols[col] ? null : compileFormula(col); // 컬럼 자리에 수식도 허용
        const e = end < 0 ? this.view.length - 1 : Math.min(end, this.view.length - 1), out = [];
        for (let i = Math.max(0, start); i <= e; i++) {
            const p = this.view[i];
            if (p < 0)
                continue; // 소계 행 제외
            if (f && !truthy(f({ ds, row: p })))
                continue;
            out.push(num(g ? g({ ds, row: p }) : ds.get(p, col)));
        }
        return out;
    }
    agg(kind, vs) {
        if (kind === 'count')
            return vs.length;
        const ok = vs.filter(v => v === v);
        if (!ok.length)
            return kind === 'sum' ? 0 : undefined;
        if (kind === 'sum')
            return ok.reduce((a, b) => a + b, 0);
        if (kind === 'avg')
            return ok.reduce((a, b) => a + b, 0) / ok.length;
        return kind === 'max' ? Math.max(...ok) : Math.min(...ok);
    }
    getSum(col, start = 0, end = -1) { return this.agg('sum', this.values(col, null, start, end)); }
    getAvg(col, start = 0, end = -1) { return this.agg('avg', this.values(col, null, start, end)); }
    getMax(col, start = 0, end = -1) { return this.agg('max', this.values(col, null, start, end)); }
    getMin(col, start = 0, end = -1) { return this.agg('min', this.values(col, null, start, end)); }
    getCaseSum(expr, col, start = 0, end = -1) { return this.agg('sum', this.values(col, expr, start, end)); }
    getCaseAvg(expr, col, start = 0, end = -1) { return this.agg('avg', this.values(col, expr, start, end)); }
    getCaseMax(expr, col, start = 0, end = -1) { return this.agg('max', this.values(col, expr, start, end)); }
    getCaseMin(expr, col, start = 0, end = -1) { return this.agg('min', this.values(col, expr, start, end)); }
    getCaseCount(expr, start = 0, end = -1) { return this.extractRows(expr).filter(i => i >= start && (end < 0 || i <= end)).length; }
}
NexaDataset.ROWTYPE_EMPTY = 0;
NexaDataset.ROWTYPE_NORMAL = 1;
NexaDataset.ROWTYPE_INSERT = 2;
NexaDataset.ROWTYPE_UPDATE = 4;
NexaDataset.ROWTYPE_DELETE = 8;
NexaDataset.ROWTYPE_GROUP = 16;
const DISPLAY = {
    normal: { format: undefined, cellType: undefined }, text: { format: undefined, cellType: undefined },
    number: { format: 'number' }, currency: { format: 'number' }, date: { format: 'date' },
    progressbar: { cellType: 'progress' }, progressbarcontrol: { cellType: 'progress' }, image: { cellType: 'image' },
    button: { cellType: 'button' }, buttoncontrol: { cellType: 'button' }, checkbox: { editor: 'check' }, checkboxcontrol: { editor: 'check' },
};
const EDIT = { none: false, normal: 'text', text: 'text', mask: 'text', date: 'date', combo: 'combo', checkbox: 'check', textarea: 'textarea', time: 'time' };
/** Nexacro Grid 호환 어댑터. 셀 번호(cellIndex)는 컬럼 정의 순서 (0부터) */
export class NexaGrid {
    constructor(grid, ds) {
        this.grid = grid;
        this.ds = ds;
        this.handlers = new Map();
        this.props = new Map();
        this.syncing = false;
        grid.setOptions({
            onCellClick: e => this.cellEvent('oncellclick', e.row, e.col),
            onCellDblClick: e => this.cellEvent('oncelldblclick', e.row, e.col),
            onHeaderClick: e => this.fire('onheadclick', { cell: this.cellIndex(e.col), col: this.cellIndex(e.col), row: -1 }) === false ? false : undefined,
            onRowFocus: e => { if (this.syncing)
                return; this.syncing = true; try {
                ds.rowposition = ds.getLogicalRow(e.row);
            }
            finally {
                this.syncing = false;
            } },
        });
        ds.addEventHandler('onrowposchanged', (_o, e) => { if (this.syncing || e.newrow < 0)
            return; this.syncing = true; try {
            grid.focusCell(ds.getPhysicalRow(e.newrow));
        }
        finally {
            this.syncing = false;
        } });
        const prevView = ds.onviewchanged;
        ds.onviewchanged = o => { prevView?.(o); this.syncView(); };
        this.syncView();
    }
    get cols() { return this.grid.opts.columns; }
    cellIndex(col) { return this.cols.findIndex(c => c.id === col); }
    colOf(cell) { return this.cols[cell]; }
    /** Dataset 논리 행 순서(필터·keystring·소계 행·insertRow 위치)를 그리드 보기로 그대로 */
    syncView() {
        const k = parseKeystring(this.ds.keystring);
        if (this.grid.getGroupBy().length)
            this.grid.setOptions({ groupBy: null });
        this.grid.setSortIndicator([...k.group, ...k.sort]);
        this.grid.setExternalOrder(this.ds.viewItems(), (i, col) => this.ds.subtotalValue(i, col));
    }
    fire(name, e) {
        let ret;
        const prop = this[name];
        if (prop)
            ret = prop(this, e);
        for (const h of this.handlers.get(name) ?? []) {
            const r = h(this, e);
            if (r !== undefined)
                ret = r;
        }
        return ret;
    }
    cellEvent(name, phys, col) { this.fire(name, { row: this.ds.getLogicalRow(phys), cell: this.cellIndex(col), col: this.cellIndex(col), columnid: col }); }
    addEventHandler(name, fn) { const a = this.handlers.get(name) ?? []; a.push(fn); this.handlers.set(name, a); return a.length - 1; }
    removeEventHandler(name, fn) { const a = this.handlers.get(name); if (!a)
        return 0; const i = a.indexOf(fn); if (i >= 0)
        a.splice(i, 1); return i >= 0 ? 1 : 0; }
    getCellCount(band = 'body') { return band === 'body' || band === 'head' ? this.cols.length : 0; }
    getCellText(row, cell) { const c = this.colOf(cell), p = this.ds.getPhysicalRow(row); return c && p >= 0 ? this.grid.cellText(p, c.id) : ''; }
    getCellValue(row, cell) { const c = this.colOf(cell); return c ? this.ds.getColumn(row, c.id) : null; }
    getCellProperty(band, cell, prop) {
        const c = this.colOf(cell);
        if (!c)
            return undefined;
        const k = `${band}|${cell}|${prop}`;
        if (this.props.has(k))
            return this.props.get(k);
        if (band === 'head' && prop === 'text')
            return c.header ?? c.id;
        if (prop === 'text')
            return 'bind:' + c.id;
        if (prop === 'align' || prop === 'textAlign')
            return c.align;
        return undefined;
    }
    /** band: 'body' | 'head'. prop: text(bind:/expr:/고정) displaytype edittype background color align mask suppress */
    setCellProperty(band, cell, prop, value) {
        const c = this.colOf(cell);
        if (!c)
            return false;
        this.props.set(`${band}|${cell}|${prop}`, value);
        const g = this.grid, id = c.id, set = (p, v) => g.setColumnProperty(id, p, v);
        const expr = (v) => typeof v === 'string' && /^expr:/i.test(v) ? compileFormula(v.slice(5)) : null;
        if (band === 'head') {
            if (prop === 'text')
                set('header', String(value));
            return true;
        }
        switch (prop) {
            case 'text': {
                const s = String(value ?? '');
                if (/^bind:/i.test(s)) {
                    const src = s.slice(5);
                    set('value', src === id ? undefined : (r, ds) => ds.get(r, src));
                }
                else {
                    const f = expr(s);
                    set('value', f ? (r, ds) => f({ ds, row: r }) : () => s);
                }
                return true;
            }
            case 'displaytype': {
                const d = DISPLAY[String(value).toLowerCase()];
                if (!d)
                    return false;
                for (const [k, v] of Object.entries(d))
                    set(k, v);
                return true;
            }
            case 'edittype': {
                const e = EDIT[String(value).toLowerCase()];
                if (e === undefined)
                    return false;
                set('editor', e);
                return true;
            }
            case 'align':
            case 'textAlign':
                set('align', String(value).split(/\s+/)[0]);
                return true;
            case 'mask':
                set('mask', String(value));
                return true;
            case 'suppress':
                set('suppress', +value > 0 || value === true);
                return true;
            case 'background':
            case 'color': {
                const rules = (c.rules ?? []).filter((r) => r.__nx !== prop);
                if (value != null && value !== '') {
                    const f = expr(value), key = prop === 'background' ? 'background' : 'color';
                    rules.push({ kind: 'when', test: () => true, style: (_v, r, ds) => { const x = f ? f({ ds, row: r }) : value; return x ? { [key]: String(x) } : null; }, __nx: prop });
                }
                set('rules', rules);
                return true;
            }
        }
        return false;
    }
    getFormatColProperty(col, prop) { const c = this.colOf(col); return prop === 'size' ? c?.width : undefined; }
    setFormatColProperty(col, prop, value) { const c = this.colOf(col); if (!c || prop !== 'size')
        return false; this.grid.setColumnProperty(c.id, 'width', +value); return true; }
    /** 현재 셀 번호 */
    getCellPos() { return this.cellIndex(this.grid.getFocusedColumn()); }
    setCellPos(cell, row) {
        const c = this.colOf(cell);
        if (!c)
            return false;
        const r = row ?? this.ds.rowposition, p = this.ds.getPhysicalRow(r < 0 ? 0 : r);
        if (p < 0)
            return false;
        this.grid.focusCell(p, c.id);
        return true;
    }
    get currentcell() { return this.getCellPos(); }
    get currentrow() { return this.ds.rowposition; }
}
